import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Check, X, Plus, Trash2, ExternalLink, HelpCircle } from "lucide-react";
import { tarifasApi, errMsg } from "./api";
import { ActivityPicker } from "./ActivityPicker";
import { PickedPreview } from "./PickedPreview";
import { PriceRows, ReasonLists } from "./ProposalCard";

const STATUS = {
  proposed: ["Propuesta IA", "bg-blue-100 text-blue-800"],
  pending: ["Pendiente de revisión", "bg-amber-100 text-amber-800"],
  confirmed: ["Confirmada", "bg-emerald-100 text-emerald-800"],
  rejected: ["Rechazada", "bg-slate-100 text-slate-600"],
};

const VIEW_LABEL = { all: "Todas las actividades del tarifario", matched: "Con actividad KimKim propuesta", unmatched: "Sin correspondencia en KimKim", confirmed: "Confirmadas" };

export const CorrespondenceList = ({ file, provider, extraction, correspondences, activities, onChanged, onLookup }) => {
  const [busy, setBusy] = useState("");
  const [picks, setPicks] = useState({});
  const [addingFor, setAddingFor] = useState(null);
  const [view, setView] = useState("matched");
  const [viewTouched, setViewTouched] = useState(false);

  const act = async (id, body, okMsg) => {
    setBusy(id);
    try {
      await tarifasApi.updateCorrespondence(id, body);
      if (okMsg) toast.success(okMsg);
      onChanged?.();
    } catch (e) {
      toast.error(errMsg(e, "No se pudo actualizar la correspondencia"));
    } finally {
      setBusy("");
    }
  };

  const remove = async (id) => {
    setBusy(id);
    try {
      await tarifasApi.deleteCorrespondence(id);
      onChanged?.();
    } finally {
      setBusy("");
    }
  };

  const addManual = async (a) => {
    const kid = picks[`add-${a.key}`];
    if (!kid) return toast.error("Elige una actividad de KimKim");
    setBusy(`add-${a.key}`);
    try {
      await tarifasApi.createCorrespondence({ provider_id: provider.id, provider_key: a.key, provider_name: a.name, provider_code: a.code, kimkim_activity_id: kid, file_id: file.id });
      toast.success("Correspondencia añadida y confirmada");
      setAddingFor(null);
      onChanged?.();
    } catch (e) {
      toast.error(errMsg(e, "No se pudo añadir"));
    } finally {
      setBusy("");
    }
  };

  const byKey = {};
  for (const c of correspondences) (byKey[c.provider_key] ||= []).push(c);
  const titleOf = (id) => activities.find((a) => a.id === id)?.title || id;
  const allActs = extraction?.activities || [];
  const hasMatch = (a) => (byKey[a.key] || []).some((c) => c.kimkim_activity_id && c.status !== "rejected");
  const isConfirmed = (a) => (byKey[a.key] || []).some((c) => c.status === "confirmed");
  const rank = (a) => Math.max(0, ...(byKey[a.key] || []).filter((c) => c.kimkim_activity_id && c.status !== "rejected").map((c) => (c.provider_marked ? 2 : 0) + (c.confidence || 0)));
  const shown = allActs
    .filter((a) => view === "all" || (view === "matched" ? hasMatch(a) && !isConfirmed(a) : view === "confirmed" ? isConfirmed(a) : !hasMatch(a)))
    .sort((a, b) => rank(b) - rank(a));
  const counts = { matched: allActs.filter((a) => hasMatch(a) && !isConfirmed(a)).length, confirmed: allActs.filter(isConfirmed).length, unmatched: allActs.filter((a) => !hasMatch(a)).length };
  // Vista inicial: la primera que tenga contenido (si no hay propuestas, mostrar directamente las "sin correspondencia")
  const autoView = counts.matched ? "matched" : counts.unmatched ? "unmatched" : counts.confirmed ? "confirmed" : "all";
  useEffect(() => { if (!viewTouched) setView(autoView); }, [autoView, viewTouched]);

  return (
    <div data-testid="correspondence-list">
      <div className="flex flex-wrap items-center gap-2 pb-2 text-xs" data-testid="correspondence-summary">
        <Badge className="rounded-md bg-blue-100 text-blue-800 hover:bg-blue-100" data-testid="corr-count-matched">{counts.matched} por revisar</Badge>
        <Badge className="rounded-md bg-emerald-100 text-emerald-800 hover:bg-emerald-100" data-testid="corr-count-confirmed">{counts.confirmed} confirmadas</Badge>
        <Badge className="rounded-md bg-slate-100 text-slate-700 hover:bg-slate-100" data-testid="corr-count-unmatched">{counts.unmatched} sin correspondencia</Badge>
        <Select value={view} onValueChange={(v) => { setViewTouched(true); setView(v); }}>
          <SelectTrigger data-testid="corr-view-filter" className="h-8 w-72 rounded-md text-xs ml-auto"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(VIEW_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {shown.length === 0 && (
        <p className="text-sm text-muted-foreground py-3" data-testid="corr-empty">
          No hay actividades en esta vista.{counts.unmatched > 0 && view !== "unmatched" ? ` Hay ${counts.unmatched} sin correspondencia: cambia el filtro a «Sin correspondencia en KimKim» para vincularlas a mano.` : ""}
        </p>
      )}
      <div className="divide-y">
        {shown.map((a) => (
          <div key={a.key} className="py-3" data-testid={`corr-activity-${a.key}`}>
            <div className="flex flex-wrap items-baseline gap-2">
              <span className="font-semibold text-sm">{a.name}</span>
              {a.code && <Badge className="rounded-md bg-slate-100 text-slate-700 hover:bg-slate-100 font-mono">{a.code}</Badge>}
              <span className="text-xs text-muted-foreground">{[a.modality !== "unknown" ? (a.modality === "private" ? "privada" : "compartida") : null, a.duration].filter(Boolean).join(" · ")}</span>
              <span className="text-xs text-muted-foreground">· {a.price_rows.length} filas de precio{a.price_rows.some((r) => r.unreadable) ? " (algunas ilegibles)" : ""}</span>
            </div>
            {a.description && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{a.description}</p>}
            <div className="mt-2 space-y-2">
              {(byKey[a.key] || []).map((c) => {
                const [label, cls] = STATUS[c.status] || [c.status, ""];
                const picked = picks[c.id];
                const repicked = picked && picked !== c.kimkim_activity_id;
                const pv = repicked ? null : c.preview;
                return (
                  <div key={c.id} className="rounded-md border p-2" data-testid={`corr-row-${c.id}`}>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge className={`rounded-md ${cls} hover:${cls.split(" ")[0]}`} data-testid={`corr-status-${c.id}`}>{label}</Badge>
                      {c.kimkim_activity_id ? (
                        <span className="text-sm flex items-center gap-1 flex-wrap" data-testid={`corr-kimkim-${c.id}`}>
                          → <strong>{c.kimkim_title || titleOf(c.kimkim_activity_id)}</strong>
                          {c.kimkim_url && <a href={c.kimkim_url} target="_blank" rel="noreferrer" className="text-primary underline text-xs flex items-center gap-0.5" data-testid={`corr-link-${c.id}`}>abrir en KimKim <ExternalLink className="h-3 w-3" /></a>}
                          {c.confidence > 0 && <span className="text-xs text-muted-foreground">({Math.round(c.confidence * 100)} %)</span>}
                          {c.provider_marked === false && <Badge className="rounded-md bg-amber-50 text-amber-800 hover:bg-amber-50">sin alias del proveedor</Badge>}
                        </span>
                      ) : (
                        <span className="text-sm text-amber-800 flex items-center gap-1"><HelpCircle className="h-3 w-3" /> sin actividad de KimKim asignada</span>
                      )}
                    </div>
                    {pv && (pv.kimkim_operator || pv.kimkim_location || pv.kimkim_price_notes) && (
                      <p className="text-xs text-muted-foreground mt-1" data-testid={`corr-kk-meta-${c.id}`}>
                        KimKim: {[pv.kimkim_location, pv.kimkim_operator ? `operador ${pv.kimkim_operator}` : null, pv.kimkim_price_notes ? `notas «${pv.kimkim_price_notes}»` : null].filter(Boolean).join(" · ")}
                      </p>
                    )}
                    {c.reason && <p className="text-xs text-muted-foreground mt-1">{c.reason}</p>}
                    {c.alternatives?.length > 0 && <p className="text-xs text-muted-foreground">Alternativas: {c.alternatives.map((x) => x.title).join(" · ")}</p>}
                    {pv && pv.rows?.length > 0 && <PriceRows rows={pv.rows} testPrefix={`corr-preview-${c.id}`} />}
                    {pv && pv.rows?.length === 0 && <p className="text-xs text-amber-800 mt-1">{pv.reasons?.[0] || "Sin tramos comparables"}</p>}
                    {pv && pv.rows?.length > 0 && <ReasonLists notes={pv.notes} reasons={pv.reasons} testId={`corr-preview-${c.id}`} />}
                    {repicked && c.status !== "confirmed" && c.status !== "rejected" && <PickedPreview fileId={file.id} providerKey={c.provider_key} kimkimId={picked} testId={`corr-picked-preview-${c.id}`} />}
                    {c.status !== "confirmed" && c.status !== "rejected" && (
                      <div className="w-full flex flex-wrap items-center gap-2 mt-2">
                        <ActivityPicker activities={activities} value={picks[c.id] ?? c.kimkim_activity_id} onChange={(v) => setPicks({ ...picks, [c.id]: v })} testId={`corr-pick-${c.id}`} onLookup={onLookup} />
                        <Button data-testid={`corr-confirm-${c.id}`} size="sm" className="rounded-md" disabled={busy === c.id || !(picks[c.id] ?? c.kimkim_activity_id)}
                          onClick={() => act(c.id, { status: "confirmed", kimkim_activity_id: picks[c.id] ?? c.kimkim_activity_id }, "Correspondencia confirmada")}>
                          <Check className="h-3 w-3 mr-1" /> Confirmar
                        </Button>
                        <Button data-testid={`corr-reject-${c.id}`} size="sm" variant="outline" className="rounded-md" disabled={busy === c.id} onClick={() => act(c.id, { status: "rejected" })}>
                          <X className="h-3 w-3 mr-1" /> Rechazar
                        </Button>
                      </div>
                    )}
                    {(c.status === "confirmed" || c.status === "rejected") && (
                      <div className="flex justify-end gap-1 mt-1">
                        <Button data-testid={`corr-reopen-${c.id}`} size="sm" variant="ghost" className="rounded-md h-7 text-xs" disabled={busy === c.id} onClick={() => act(c.id, { status: "pending" })}>Revisar</Button>
                        <Button data-testid={`corr-clear-${c.id}`} size="sm" variant="ghost" className="rounded-md h-7 text-xs" disabled={busy === c.id} onClick={() => act(c.id, { status: "pending", clear_activity: true })}>Quitar actividad</Button>
                        <Button data-testid={`corr-delete-${c.id}`} size="sm" variant="ghost" className="rounded-md h-7 text-red-600" disabled={busy === c.id} onClick={() => remove(c.id)}><Trash2 className="h-3 w-3" /></Button>
                      </div>
                    )}
                  </div>
                );
              })}
              {addingFor === a.key ? (
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <ActivityPicker activities={activities} value={picks[`add-${a.key}`]} onChange={(v) => setPicks({ ...picks, [`add-${a.key}`]: v })} testId={`corr-add-pick-${a.key}`} onLookup={onLookup} />
                    <Button data-testid={`corr-add-save-${a.key}`} size="sm" className="rounded-md" onClick={() => addManual(a)} disabled={busy === `add-${a.key}`}><Check className="h-3 w-3 mr-1" /> Añadir</Button>
                    <Button size="sm" variant="ghost" className="rounded-md" onClick={() => setAddingFor(null)}>Cancelar</Button>
                  </div>
                  {picks[`add-${a.key}`] && <PickedPreview fileId={file.id} providerKey={a.key} kimkimId={picks[`add-${a.key}`]} testId={`corr-add-preview-${a.key}`} />}
                </div>
              ) : (
                <Button data-testid={`corr-add-${a.key}`} size="sm" variant="ghost" className="rounded-md h-7 text-xs" onClick={() => setAddingFor(a.key)}>
                  <Plus className="h-3 w-3 mr-1" /> Vincular con otra actividad de KimKim
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
